import type { ICompany } from "@/models/Company";

type CrmIntegrated = ICompany["integratedPlatforms"]["crm"];
type AdsIntegrated = ICompany["integratedPlatforms"]["ads"];
type IntegratedPlatforms = ICompany["integratedPlatforms"];

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === "object" && !Array.isArray(value);
}

function normalizeCrm(crm: unknown): CrmIntegrated {
  if (isRecord(crm)) {
    return {
      houseCallPro: Boolean(crm.houseCallPro),
      serviceTitan: Boolean(crm.serviceTitan),
      serviceFusion: Boolean(crm.serviceFusion),
      jobber: Boolean(crm.jobber),
    };
  }
  // legacy boolean or missing → no per-platform flags set
  return {};
}

function normalizeAds(ads: unknown): AdsIntegrated {
  if (isRecord(ads)) {
    return {
      googleAds: Boolean(ads.googleAds),
      microsoftAds: Boolean(ads.microsoftAds),
      metaAds: Boolean(ads.metaAds),
      yelpAds: Boolean(ads.yelpAds),
      angiAds: Boolean(ads.angiAds),
    };
  }
  // legacy boolean or missing → no per-platform flags set
  return {};
}

/**
 * Ensures `company.integratedPlatforms` conforms to the new object shape, while
 * safely handling legacy boolean values from older records.
 */
export function ensureIntegratedPlatforms(company: ICompany): IntegratedPlatforms {
  const raw = (company as unknown as { integratedPlatforms?: unknown }).integratedPlatforms;

  if (!isRecord(raw)) {
    const normalized: IntegratedPlatforms = { crm: {}, ci: false, ads: {} };
    (company as unknown as { integratedPlatforms: IntegratedPlatforms }).integratedPlatforms = normalized;
    return normalized;
  }

  const normalized: IntegratedPlatforms = {
    crm: normalizeCrm(raw.crm),
    ci: Boolean(raw.ci),
    ads: normalizeAds(raw.ads),
  };

  (company as unknown as { integratedPlatforms: IntegratedPlatforms }).integratedPlatforms = normalized;
  return normalized;
}

